import { useEffect, useCallback, useMemo, useState } from 'react';

import axios, { endpoints } from 'src/utils/axios';

import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';
import { AuthStateType } from '../types';

const initialState: AuthStateType = {
  session: null,
  loading: true,
};

export const STORAGE_KEY = 'accessToken';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, setState] = useState(initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const response = await axios.get(endpoints.auth.current);

        const { session } = response.data;

        setState({ loading: false, session });
      } else {
        setState({ loading: false, session: null });
      }
    } catch (error) {
      console.error(error);
      setState({ loading: false, session: null });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    const response = await axios.post(endpoints.auth.login, { email, password });

    const { accessToken, session } = response.data;
    setSession(accessToken);

    setState((s) => ({ ...s, session }));

    return session;
  }, []);

  // LOGIN with a token handed back by the Google OAuth redirect
  const loginFromToken = useCallback(async (accessToken: string) => {
    setSession(accessToken);

    const response = await axios.get(endpoints.auth.current);

    const { session } = response.data;

    setState((s) => ({ ...s, loading: false, session }));

    return session;
  }, []);

  // REGISTER
  const register = useCallback(
    async (name: string, phone: string, email: string, password: string, organizationName: string) => {
      const response = await axios.post(endpoints.auth.register, {
        name,
        phone,
        email,
        password,
        organizationName,
      });

      const { accessToken, session } = response.data;

      setSession(accessToken);

      setState((s) => ({ ...s, session }));
    },
    []
  );

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);

    setState((s) => ({ ...s, session: null }));
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.session ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      session: state.session,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      loginFromToken,
      register,
      logout,
    }),
    [login, loginFromToken, logout, register, state.session, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
